/**
 * NX results for a person (server 064/065): what an action or survey found,
 * as tables and cards instead of JSON. The reading is nx-results.ts (tested);
 * this only draws it. The raw answer stays one click away, for support.
 */

import { Download } from 'lucide-react';
import { useState } from 'react';

import { Button, Pill, type PillState } from './components/index.ts';
import {
  CAUSE_WORDS,
  alarms,
  diagnosisRows,
  diagnosisSummary,
  disks,
  formatBytes,
  retention,
  statusCounts,
  type Cause,
} from './nx-results.ts';
import { resultLogin, type HostLogins } from './nx-logins.ts';

const CAUSE_STATE: Record<Cause, PillState> = {
  unreachable: 'fault',
  camera_fault: 'fault',
  wrong_password: 'warning',
  unknown: 'stale',
};

// What NX's answer means, stated as fact: this is the customer's system, and
// CosMon reports it as NX does (owner, 2026-09-29: "present data as it is").
const CAUSE_HINT: Record<Cause, string> = {
  unreachable: 'NX gets no answer from the camera at this address.',
  camera_fault: 'The camera answers, but refused a request NX made of it.',
  wrong_password: 'The camera answers, but refuses the login NX holds for it.',
  unknown: 'NX gave a reason CosMon does not recognise; the raw result has it in full.',
};

/**
 * Which NX user it ran as, from which credential, and a way to change it: the
 * Moorebank survey said "login: HTTP 404" and nothing about the old `cosmon`
 * user it had tried (2026-10-20).
 */
function LoginLine({
  result,
  logins,
  onChangeLogin,
}: {
  result: Record<string, unknown>;
  logins: HostLogins | undefined;
  onChangeLogin: (() => void) | undefined;
}): React.JSX.Element {
  const r = resultLogin(result, logins ?? null);
  return (
    <p className="small">
      {r.user === null ? (
        r.outcome?.startsWith('none') ? (
          <strong className="text-warn">No NX login was attached, so the agent did not log in.</strong>
        ) : (
          <span className="muted">The result does not say which NX user it used.</span>
        )
      ) : (
        <>
          {r.failed ? 'Tried to log in as ' : 'Logged in as '}
          <strong className="mono">{r.user}</strong> — the {r.tier} login
          {r.from !== null && (
            <>
              , from the credential “{r.from.label}”
            </>
          )}
          .
        </>
      )}
      {r.changedSince !== null && <span className="muted"> {r.changedSince} Ask again to try it.</span>}
      {onChangeLogin !== undefined && (
        <>
          {' '}
          <button type="button" className="agent-log-pick" onClick={onChangeLogin}>
            Change logins
          </button>
        </>
      )}
    </p>
  );
}

function Diagnosis({ result }: { result: Record<string, unknown> }): React.JSX.Element {
  const rows = diagnosisRows(result);
  const offline = typeof result['_offline'] === 'number' ? result['_offline'] : rows.length;
  return (
    <div className="stack-tight">
      <p>
        <strong>{diagnosisSummary(rows)}</strong>
        {offline > rows.length && <span className="muted"> The first {rows.length} of {offline} were diagnosed.</span>}
      </p>
      {rows.length > 0 && (
        <table className="dense">
          <thead>
            <tr>
              <th>Camera</th>
              <th>Address</th>
              <th>Why it is offline</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={r.duplicateOf !== null ? 'muted' : undefined}>
                <td>
                  {r.name}
                  {/* An unnamed camera's NX name is its model: say the vendor, not the model twice. */}
                  <div className="small muted">{r.name === r.model ? (r.vendor ?? '') : [r.vendor, r.model].filter(Boolean).join(' ')}</div>
                </td>
                <td className="mono small">{r.ip ?? '—'}</td>
                <td>
                  {r.duplicateOf !== null ? (
                    <Pill
                      state="stale"
                      reason={`NX has another entry at ${r.ip ?? 'this address'}, ${r.duplicateOf.name}, and that one is ${r.duplicateOf.status.toLowerCase()}. This entry is offline.`}
                    >
                      Same address as {r.duplicateOf.name}
                    </Pill>
                  ) : (
                    <Pill state={CAUSE_STATE[r.cause]} reason={CAUSE_HINT[r.cause]}>
                      {CAUSE_WORDS[r.cause]}
                    </Pill>
                  )}
                  <div className="small muted">{r.detail}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Storage({ result }: { result: Record<string, unknown> }): React.JSX.Element {
  const list = disks(result);
  const kept = retention(result);
  return (
    <div className="stack-tight">
      {list.map((d) => {
        const used = d.totalB !== null && d.freeB !== null ? d.totalB - d.freeB : null;
        const pct = used !== null && d.totalB ? Math.round((used / d.totalB) * 100) : null;
        return (
          <div key={d.id} className="nx-disk">
            <div className="row-tight">
              <strong className="mono">{d.path}</strong>
              <Pill state={d.online ? 'ok' : 'fault'}>{d.online ? 'online' : 'offline'}</Pill>
              {d.recording ? <Pill state="ok">recording here</Pill> : <Pill>not used for recording</Pill>}
              {d.system && <Pill>system disk</Pill>}
              {d.backup && <Pill>backup</Pill>}
              {d.recording && !d.writable && <Pill state="fault">not writable</Pill>}
              {d.flags.map((f) => (
                <Pill key={f} state="warning">
                  {f}
                </Pill>
              ))}
            </div>
            {pct !== null && (
              <div className="nx-meter" role="img" aria-label={`${pct}% used`}>
                <span style={{ width: `${pct}%` }} />
              </div>
            )}
            <div className="small muted">
              {formatBytes(used)} used of {formatBytes(d.totalB)} · {formatBytes(d.freeB)} free · {formatBytes(d.reservedB)} kept
              free by NX
            </div>
            {d.recording && d.freeB !== null && d.reservedB !== null && d.freeB <= d.reservedB * 1.05 && (
              <div className="small">
                Full, as a recorder should be: NX is overwriting its oldest footage to stay at the free space it keeps.
              </div>
            )}
          </div>
        );
      })}
      {kept.length > 0 && (
        <>
          <strong>Footage held per camera</strong>
          <table className="dense">
            <thead>
              <tr>
                <th>Camera</th>
                <th>Days</th>
                <th>Size</th>
                <th>Recorded</th>
              </tr>
            </thead>
            <tbody>
              {kept.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="mono">{r.days.toFixed(0)}</td>
                  <td className="mono">{formatBytes(r.bytes)}</td>
                  <td
                    className="mono"
                    title="How much of that span has footage: 100% is continuous; lower is gaps or motion-only recording"
                  >
                    {r.coverage === null ? '—' : `${Math.round(r.coverage * 100)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function Survey({ result }: { result: Record<string, unknown> }): React.JSX.Element {
  const counts = statusCounts(result);
  const list = alarms(result);
  const survey = result['_survey'] as { install?: { port?: number; state?: string } } | undefined;
  return (
    <div className="stack-tight">
      <p className="small muted">
        Login: {String(result['_login'] ?? '—')} · service {survey?.install?.state ?? '?'} on port {survey?.install?.port ?? '?'}
      </p>
      {counts.length > 0 && (
        <div className="row-tight">
          <strong>Cameras</strong>
          {counts.map(([status, n]) => (
            <Pill key={status} state={status === 'Recording' || status === 'Online' ? 'ok' : status === 'Offline' ? 'fault' : 'stale'}>
              {n} {status.toLowerCase()}
            </Pill>
          ))}
        </div>
      )}
      <strong>NX’s own alarms</strong>
      {list.length === 0 ? (
        <p className="small muted">None.</p>
      ) : (
        <table className="dense">
          <tbody>
            {list.map((a, i) => (
              <tr key={i}>
                <td>
                  <Pill state={a.level === 'error' ? 'fault' : 'warning'}>{a.level}</Pill>
                </td>
                <td>{a.name}</td>
                <td>{a.text}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** What an NX action or survey found. `raw` is the stored JSON text. */
export function NxResultView({
  raw,
  label,
  logins,
  onChangeLogin,
}: {
  raw: string;
  label: string;
  /** The host's NX logins now; the result says when they have changed since. */
  logins?: HostLogins | undefined;
  onChangeLogin?: (() => void) | undefined;
}): React.JSX.Element {
  const [showRaw, setShowRaw] = useState(false);
  let result: Record<string, unknown> | null = null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) result = parsed as Record<string, unknown>;
  } catch {
    result = null;
  }
  const action = (result?.['_action'] as { action?: string } | undefined)?.action;
  const failed = typeof result?.['_error'] === 'string' ? (result['_error'] as string) : typeof result?.['_login'] === 'string' && (result['_login'] as string).startsWith('failed') ? (result['_login'] as string) : null;

  const download = (): void => {
    const url = URL.createObjectURL(new Blob([raw], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `nx-${action ?? 'survey'}-${label}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <div className="stack-tight nx-result">
      {result === null ? (
        <p>The agent sent something CosMon cannot read; see the raw result.</p>
      ) : failed !== null ? (
        <p>
          <strong>It did not run:</strong> {failed}
        </p>
      ) : action === 'diagnose_offline' ? (
        <Diagnosis result={result} />
      ) : action === 'storage_detail' ? (
        <Storage result={result} />
      ) : (
        <Survey result={result} />
      )}
      {result !== null && <LoginLine result={result} logins={logins} onChangeLogin={onChangeLogin} />}
      <div className="row-tight">
        <Button size="sm" variant="ghost" onClick={() => setShowRaw((v) => !v)}>
          {showRaw ? 'Hide the raw result' : 'Show the raw result'}
        </Button>
        <Button size="sm" variant="ghost" icon={<Download size={14} />} onClick={download}>
          Download
        </Button>
      </div>
      {showRaw && (
        <pre className="agent-log" tabIndex={0}>
          {(() => {
            try {
              return JSON.stringify(JSON.parse(raw), null, 2);
            } catch {
              return raw;
            }
          })()}
        </pre>
      )}
    </div>
  );
}
